import {
  Autocomplete,
  Box,
  DEPRECATED_REACT_PROPS,
  Popper,
  PopperProps,
  Stack,
  styled,
  TextField,
  Theme,
  Typography,
} from '@chronosphereio/chrono-ui';
import { getLocalIANATimeZone } from '@chronosphereio/core';
import React, { useState } from 'react';
import { useTimeZoneDetails } from '../DateTimeRangePicker/utils/useTimeZoneDetails';
import { loadSupportedTimeZones, loadTimeZoneOptions, TimeZoneOption, USER_TIME_ZONE_ID } from './timezone-model';

const AUTOCOMPLETE_DROPDOWN_WIDTH_PX = 384;
const AUTOCOMPLETE_INPUT_WIDTH_PX = 272;

export const MODAL_INPUT_WIDTH_PX = 307;
const MODAL_DROPDOWN_WIDTH_PX = MODAL_INPUT_WIDTH_PX;

function getComponentProps(dropdownWidth: number, isAutocomplete = false) {
  if (isAutocomplete) {
    return {
      paper: { sx: { width: `${dropdownWidth}px` } },
    };
  }

  return {
    paper: {
      sx: {
        width: `${dropdownWidth}px`,
        boxShadow: 'none',
        backgroundColor: (theme: Theme) => theme.palette.background.default,
        backgroundImage: 'none',
      },
    },
  };
}

function getRightPopper(inputWidth: number, dropdownWidth: number, isAutocomplete = true) {
  return function RightPopper(props: PopperProps) {
    return (
      <Popper
        {...DEPRECATED_REACT_PROPS}
        {...props}
        disablePortal={!isAutocomplete}
        placement="bottom-end"
        modifiers={[
          {
            name: 'offset',
            options: { offset: [inputWidth - dropdownWidth, 0] },
          },
        ]}
      />
    );
  };
}

function LeftPopper(props: PopperProps) {
  return <Popper {...props} placement="bottom-start" {...DEPRECATED_REACT_PROPS} />;
}

/**
 * Formats a timezone option label with timezone name, abbreviation, and UTC offset.
 */
export function formatTimeZoneOptionLabel(option: TimeZoneOption): string {
  const isSpecialCase =
    option.title === 'Coordinated Universal Time' ||
    option.title === 'Local time' ||
    option.title === 'Account time zone';
  const parts = [option.title];
  if (option.subtitle && option.offset) {
    const prefix = isSpecialCase ? ' - ' : ', ';
    parts.push(`${prefix}${option.subtitle} - ${option.offset}`);
  } else if (option.subtitle) {
    const prefix = isSpecialCase ? ' - ' : ', ';
    parts.push(`${prefix}${option.subtitle}`);
  }
  return parts.join('');
}

interface TimeZoneAutocomplete {
  /**
   * The selected time zone. If not provided, defaults to the local time zone.
   */
  timeZone?: string;
  /**
   * The user time zone, used in date time picker.
   */
  userTimeZone?: string;
  /**
   * Callback for when time zone changes.
   */
  onChange: (timezone: string) => void;
  /**
   * Alignment of the dropdown menu.
   */
  align: 'left' | 'right';
  /**
   * Mode of the time zone autocomplete.
   * The modal is mainly used in the DateTimeRangePicker to enable time zone override feature.
   * @default autocomplete
   */
  mode?: 'autocomplete' | 'modal';
  /**
   * Whether the autocomplete is disabled.
   */
  disabled?: boolean;
  /**
   * Whether to show the account time zone option.
   * @default false
   */
  showAccountTimeZone?: boolean;
  /**
   * Custom label for the input field. If not provided, defaults to
   * "Page time zone" or "Time zone" based on the feature flag.
   */
  label?: string;
}

/**
 * Autocomplete that selects the global Timezone
 */
export function TimeZoneAutocomplete({
  userTimeZone,
  timeZone,
  onChange,
  align,
  mode = 'autocomplete',
  disabled = false,
  showAccountTimeZone = false,
  label: labelProp,
}: TimeZoneAutocomplete) {
  const isAutocomplete = mode === 'autocomplete';
  const showAcctTimeZone = showAccountTimeZone || !isAutocomplete;

  const dropdownWidth = isAutocomplete ? AUTOCOMPLETE_DROPDOWN_WIDTH_PX : MODAL_DROPDOWN_WIDTH_PX;
  const inputWidth = isAutocomplete ? AUTOCOMPLETE_INPUT_WIDTH_PX : MODAL_DROPDOWN_WIDTH_PX;

  const [dropdownOpen, setDropdownOpen] = useState(!isAutocomplete);

  const userTimeZoneDetails = useTimeZoneDetails({
    timeZone: userTimeZone,
  });

  const browserTimeZoneId = getLocalIANATimeZone();
  const { timeZoneOptionList: TIMEZONE_LIST, timeZoneOptionDictionary: TIMEZONE_DICTIONARY } = loadTimeZoneOptions({
    supportedTimeZones: loadSupportedTimeZones(),
    browserTimeZoneId,
    userTimeZoneDetails: showAcctTimeZone ? userTimeZoneDetails : undefined,
  });

  // If the selected time zone is same as account time zone, then show account time zone instead
  if (showAcctTimeZone && userTimeZoneDetails.IANA === timeZone) {
    timeZone = USER_TIME_ZONE_ID;
  }
  // If the selected time zone matches the browser's local timezone, map it to 'local' for display
  if (timeZone === browserTimeZoneId) {
    timeZone = 'local';
  }

  return (
    <Autocomplete
      disabled={disabled}
      open={isAutocomplete ? dropdownOpen : true}
      forcePopupIcon={isAutocomplete} // Hides dropdown arrow if mode is modal
      onOpen={() => setDropdownOpen(true)}
      onClose={() => setDropdownOpen(false)}
      options={TIMEZONE_LIST}
      value={TIMEZONE_DICTIONARY[timeZone ?? ''] ?? TIMEZONE_DICTIONARY.local}
      onChange={(_, value) => {
        if (userTimeZoneDetails.IANA && value.id === USER_TIME_ZONE_ID) {
          onChange(userTimeZoneDetails.IANA);
        } else {
          onChange(value.id);
        }
      }}
      onKeyDown={(e) => e.stopPropagation()} // Prevents other inputs from listening to key down when mode is modal
      getOptionLabel={(option) => formatTimeZoneOptionLabel(option)}
      multiple={false}
      disableClearable={true}
      freeSolo={false}
      groupBy={(option) => option.group}
      filterOptions={(options, state) => {
        const lowercaseInputValue = state.inputValue.toLowerCase();
        return options.filter((option) => {
          return (
            option.title.toLowerCase().includes(lowercaseInputValue) ||
            option.subtitle?.toLowerCase().includes(lowercaseInputValue) ||
            option.offset.toLowerCase().includes(lowercaseInputValue)
          );
        });
      }}
      renderGroup={(params) => (
        <li key={params.group}>
          {params.group.length > 0 && (
            <GroupHeader>
              <Typography variant="body2" color={(theme) => theme.palette.text.legacySecondary}>
                <strong>{params.group}</strong>
              </Typography>
            </GroupHeader>
          )}
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderInput={(params) => {
        return (
          <Box sx={{ position: 'relative' }}>
            <TextField
              {...params}
              label={labelProp ?? 'Page time zone'}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>
        );
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Option>
            <Stack direction="column">
              {option.title}
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.text.legacySecondary,
                })}
              >
                {option.subtitle}
              </Typography>
            </Stack>
            {option.offset}
          </Option>
        </li>
      )}
      fullWidth
      blurOnSelect
      componentsProps={getComponentProps(dropdownWidth, isAutocomplete)}
      PopperComponent={align === 'left' ? LeftPopper : getRightPopper(inputWidth, dropdownWidth, isAutocomplete)}
    />
  );
}

const Option = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  whiteSpace: 'nowrap',
  width: '100%',
});

const GroupHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.border.default}`,
}));

const GroupItems = styled('ul')({
  padding: 0,
});
